// Small display formatters for the talent dashboard. Pure, so they are unit-tested
// (format.test.ts) rather than eyeballed. Plain hyphens only: a long dash in anything a
// talent reads fails the repo-wide copy gate (root CLAUDE.md §1.13).

/** Whole US dollars with thousands separators: 1500 -> "$1,500". */
export function formatUsd(amount: number): string {
  return `$${Math.round(amount).toLocaleString("en-US")}`;
}

/** A 0-24 hour as a clock label: 9 -> "09:00", 24 -> "24:00" (midnight, the window's end). */
export function formatHour(hour: number): string {
  return `${String(hour).padStart(2, "0")}:00`;
}

/** Up to two initials for an avatar: "dana_designs" -> "DD", "nooranimate" -> "NO". */
export function initials(id: string): string {
  const words = id
    .replace(/[^\p{L}\p{N}]+/gu, " ")
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (words.length === 0) return "?";
  if (words.length === 1) return words[0].slice(0, 2).toUpperCase();
  return (words[0][0] + words[1][0]).toUpperCase();
}

/** The project timer's countdown, coarse on purpose: "2d 4h", "3h 12m", "3h", "42m",
 *  "under a minute". Every unit is floored, so the label never promises more time than is
 *  left. "" at or past zero (or for junk), where the caller stops showing a timer. */
export function timeLeft(ms: number): string {
  if (!(ms > 0)) return "";
  const minutes = Math.floor(ms / 60_000);
  if (minutes < 1) return "under a minute";
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return minutes % 60 ? `${hours}h ${minutes % 60}m` : `${hours}h`;
  const days = Math.floor(hours / 24);
  return hours % 24 ? `${days}d ${hours % 24}h` : `${days}d`;
}

/** A moment on the talent's own clock: "4:30 PM" today, "Oct 2, 9:05 AM" on another day;
 *  "" when unreadable. */
export function clockTime(iso: string, now: Date = new Date()): string {
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return "";
  // Newer ICU builds put a narrow no-break space before "PM"; one plain space everywhere.
  const time = at
    .toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" })
    .replace(/\s+/g, " ");
  if (at.toDateString() === now.toDateString()) return time;
  return `${at.toLocaleDateString("en-US", { month: "short", day: "numeric" })}, ${time}`;
}

/** "1 talent" / "3 talents" style counts. */
export function plural(count: number, one: string, many: string = `${one}s`): string {
  return `${count} ${count === 1 ? one : many}`;
}
